import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import AxiosService from "../../services/AxiosHelper";
import { toast } from "react-toastify";

const baseURL = process.env.REACT_APP_BASEURL;

/**
 * Fetch current subscription status for the master customer
 */
export const getSubscriptionStatus = createAsyncThunk(
  "subscription/getStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "get",
        url: `${baseURL}/subscription/status`,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch subscription status");
    }
  }
);

/**
 * Fetch available subscription plans
 */
export const getSubscriptionPlans = createAsyncThunk(
  "subscription/getPlans",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "get",
        url: `${baseURL}/subscription/plans`,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch plans");
    }
  }
);

/**
 * Create a Stripe checkout session for a specific plan
 */
export const createCheckoutSession = createAsyncThunk(
  "subscription/createCheckout",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "post",
        url: `${baseURL}/subscription/checkout`,
        data: { planId },
      });
      
      // If the API returns a Stripe URL, redirect the user
      if (response.data?.data?.url) {
        window.location.href = response.data.data.url;
      }
      
      return response.data.data;
    } catch (err) {
      const message = err.response?.data?.message || "Checkout failed";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

/**
 * Cancel the current subscription
 */
export const cancelSubscription = createAsyncThunk(
  "subscription/cancel",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "post",
        url: `${baseURL}/subscription/cancel`,
      });
      toast.success("Subscription canceled successfully");
      dispatch(getSubscriptionStatus()); // Refresh status
      return response.data.data;
    } catch (err) {
      const message = err.response?.data?.message || "Cancellation failed";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState: {
    status: null,
    plans: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearSubscriptionError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptionStatus.fulfilled, (state, action) => {
        state.status = action.payload;
        state.loading = false;
      })
      .addCase(getSubscriptionPlans.fulfilled, (state, action) => {
        state.plans = action.payload || [];
        state.loading = false;
      })
      .addCase(createCheckoutSession.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(cancelSubscription.fulfilled, (state) => {
        state.loading = false;
      });

    // Unified loading and error matchers
    builder.addMatcher(
      (action) => action.type.startsWith("subscription/") && action.type.endsWith("/pending"),
      (state) => {
        state.loading = true;
        state.error = null;
      }
    );
    builder.addMatcher(
      (action) =>
        action.type.startsWith("subscription/") &&
        (action.type.endsWith("/fulfilled") || action.type.endsWith("/rejected")),
      (state, action) => {
        state.loading = false;
        if (action.type.endsWith("/rejected")) {
          state.error = action.payload;
        }
      }
    );
  },
});

export const { clearSubscriptionError } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
